import AttenderInput from '@/components/Record/SubjectContent/AttenderInput';
import ArrowButton from '@/components/Record/SubjectContent/ArrowButton';
import React, { useEffect, useRef, useState } from 'react';
import { RecordFilter } from '@/types/api';
import useClickOutside from '@/components/Record/SubjectContent/@hooks/useClickOutside';
import { useFormContext } from 'react-hook-form';
import AttachmentList from './AttachmentList';
import DataItem from '@/components/Record/SubjectContent/AttachmentConnect/DataItem';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'usehooks-ts';
import { useAttachmentKeyboard } from '@/components/Record/SubjectContent/@hooks/useAttachmentKeyboard';

const registerName = 'searchFields.dataSearch';

const AttachmentConnect = () => {
  const { t } = useTranslation();
  const [isFocus, setIsFocus] = useState(false);
  const [searchFocusIndex, setSearchFocusIndex] = useState(-1);
  const [relationFocusIndex, setRelationFocusIndex] = useState(-1);
  const [currentSearchData, setCurrentSearchData] = useState<any[]>([]);
  const { watch, getValues, setValue } = useFormContext<RecordFilter>();
  const ref = useRef<HTMLDivElement>(null);
  const searchString = watch(registerName);
  const debouncedValue = useDebounce(searchString, 500);
  const handleDelete = (id: string) => {
    const updated = getValues('relations').filter((d) => d.id !== id);
    setValue('relations', updated);
    // Reset relation focus if item was deleted
    if (relationFocusIndex >= updated.length) {
      setRelationFocusIndex(Math.max(-1, updated.length - 1));
    }
  };

  const handleSelectAttachment = (attachment: any) => {
    const current = getValues('relations');
    const exists = current.some((d) => d.id === attachment.id);

    if (!exists) {
      setValue('relations', [
        ...current,
        {
          type: attachment.type === 'approval' ? 'approval' : 'talkFlow',
          id: attachment.id,
          name: attachment.name ?? '(제목 없는 문서)',
          link:
            attachment.type === 'approval'
              ? attachment.link
              : `${process.env.NEXT_PUBLIC_ORIGIN_URL}/talkflow/${attachment.id}`,
        },
      ]);
      setValue(registerName, '');
    }
  };

  const data = watch('relations');

  const { handleKeyDown } = useAttachmentKeyboard({
    isFocus,
    debouncedValue,
    searchString,
    attachmentList: currentSearchData,
    relationsList: data,
    searchFocusIndex,
    relationFocusIndex,
    registerName,
    setSearchFocusIndex,
    setRelationFocusIndex,
    setIsFocus,
    setValue,
    handleSelectAttachment,
    handleDeleteRelation: handleDelete,
  });

  // Reset search focus when search data changes
  useEffect(() => {
    setSearchFocusIndex(-1);
  }, [debouncedValue]);

  // Reset relation focus when input has content
  useEffect(() => {
    if (searchString.length > 0) {
      setRelationFocusIndex(-1);
    }
  }, [searchString]);
  useClickOutside(ref, () => {
    setIsFocus(false);
    setValue('searchFields.dataSearch', '');
    setSearchFocusIndex(-1);
    setRelationFocusIndex(-1);
  });
  return (
    <div ref={ref}>
      <AttenderInput
        isFocus={isFocus}
        registerName={registerName}
        placeholder={t('품의나 문서를 검색해 주세요.')}
        leftChildren={data.map((item, index) => (
          <DataItem
            key={item.id}
            item={item}
            onDelete={() => handleDelete(item.id)}
            isFocused={relationFocusIndex === index}
          />
        ))}
        rightChildren={
          <ArrowButton onOpen={() => setIsFocus(!isFocus)} open={isFocus} />
        }
        handleKeyDown={handleKeyDown}
        onFocus={() => setIsFocus(true)}
      />
      {isFocus && (
        <AttachmentList
          searchFocusIndex={searchFocusIndex}
          onSelectAttachment={handleSelectAttachment}
          onDataChange={setCurrentSearchData}
          onResetFocusIndex={() => setSearchFocusIndex(-1)}
        />
      )}
    </div>
  );
};
export default AttachmentConnect;
